import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  applyDocumentLanguage,
  LOCALE_STORAGE_KEY,
  readStoredLanguage,
  type AppLanguageCode,
} from './languages';
import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import cy from './locales/cy.json';
import it from './locales/it.json';
import de from './locales/de.json';
import ar from './locales/ar.json';
import uiEn from './locales/ui/en.json';
import uiEs from './locales/ui/es.json';
import uiFr from './locales/ui/fr.json';
import uiCy from './locales/ui/cy.json';
import uiIt from './locales/ui/it.json';
import uiDe from './locales/ui/de.json';
import uiAr from './locales/ui/ar.json';

const initialLanguage = readStoredLanguage();
applyDocumentLanguage(initialLanguage);

const uiByLang: Record<AppLanguageCode, Record<string, string>> = {
  en: uiEn as Record<string, string>,
  es: uiEs as Record<string, string>,
  fr: uiFr as Record<string, string>,
  cy: uiCy as Record<string, string>,
  it: uiIt as Record<string, string>,
  de: uiDe as Record<string, string>,
  ar: uiAr as Record<string, string>,
};

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en, ui: uiByLang.en },
    es: { translation: es, ui: uiByLang.es },
    fr: { translation: fr, ui: uiByLang.fr },
    cy: { translation: cy, ui: uiByLang.cy },
    it: { translation: it, ui: uiByLang.it },
    de: { translation: de, ui: uiByLang.de },
    ar: { translation: ar, ui: uiByLang.ar },
  },
  lng: initialLanguage,
  fallbackLng: 'en',
  defaultNS: 'translation',
  interpolation: { escapeValue: false },
  returnNull: false,
  // English phrase keys that are missing still render as English.
  parseMissingKeyHandler: (key) => key,
});

i18n.on('languageChanged', (lng) => {
  applyDocumentLanguage(lng);
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, lng);
  } catch {
    // Ignore storage errors.
  }
});

/** Fill remaining `{{var}}` placeholders (i18next skips this for missing-key / wrong-key lookups). */
function applyUiPlaceholders(
  text: string,
  options?: Record<string, string | number>,
): string {
  if (!options || !text.includes('{{')) return text;
  let out = text;
  for (const [key, value] of Object.entries(options)) {
    out = out.split(`{{${key}}}`).join(String(value));
  }
  return out;
}

/** Translate a UI chrome English phrase via the `ui` namespace. */
export function translateUi(
  text: string,
  options?: Record<string, string | number>,
): string {
  if (!text) return text;
  // Never translate CSS colors / utilities — locale files previously mangled these (e.g. #34d399 → #34د399).
  if (
    /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(text) ||
    /^(rgba?|hsla?)\(/i.test(text)
  ) {
    return text;
  }
  const translated = i18n.t(text, { ns: 'ui', defaultValue: text, ...options });
  return applyUiPlaceholders(translated, options);
}

export default i18n;
